import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import * as actions from 'global/actions/authActions';
import { AUTH_TWO_FACTOR_TIMEOUT_ERROR } from 'global/constants';
import { useActions } from 'hooks/useActions';
import { useAppContext } from 'hooks/useContext';
import { Form } from 'components/FormComponents/FormComponents';
import * as strings from './strings';
import Path from 'routes/Path';
import apiErrors from 'api/apiErrors';
import SignInWithGoogle from 'components/ExternalSignIn/SignInWithGoogle';
import SignInWithLinkedIn from 'components/ExternalSignIn/SignInWithLinkedIn';
import Divider from 'components/Divider/Divider';
import { Container, Wrapper } from 'components/Layout/Layout';
import { ButtonsContainer, RectangleButton } from 'components/Button/RectangleButton';
import {
  AuthPageBackgroundImage,
  AuthContainer,
  Heading,
  InputContainer,
  InputField,
  Icon,
  InfoText,
  LinkToPage,
  AuthPageGradient,
} from './styles';

export default function LoginPage() {
  const navigate = useNavigate();

  const { authLoading, authError, authTwoFactor } = useSelector((state) => state.auth);
  const { clearAuthError, login, loginTwoFactor } = useActions(actions);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const { language } = useAppContext();

  return (
    <Wrapper isNewPage>
      <AuthPageBackgroundImage>
        <AuthPageGradient />
      </AuthPageBackgroundImage>
      <Container column useZIndex="1">
        <AuthContainer>
          <Heading>{strings.headerLabel[language]}</Heading>
          {authTwoFactor ? (
            <Form onChange={() => authError && clearAuthError()} onSubmit={() => loginTwoFactor(twoFactorCode)}>
              <InputContainer>
                <Icon className="fa-solid fa-qrcode" />
                <InputField
                  type="text"
                  placeholder={strings.twoFaPlaceholder[language]}
                  value={twoFactorCode}
                  hasError={authError}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                />
              </InputContainer>
              <ButtonsContainer padding="20px 0 0">
                <RectangleButton disabled={!twoFactorCode || authError} type="submit">
                  {strings.loginLabel[language]}
                </RectangleButton>
              </ButtonsContainer>
            </Form>
          ) : (
            <Form onChange={() => authError && clearAuthError()} onSubmit={() => login(email, password)}>
              <InputContainer hasError={authError}>
                <Icon className="fa-solid fa-user" />
                <InputField
                  type="email"
                  placeholder={strings.emailPlaceholder[language]}
                  value={email}
                  hasError={authError}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </InputContainer>
              <InputContainer hasError={authError}>
                <Icon className="fa-solid fa-lock" />
                <InputField
                  type="password"
                  placeholder={strings.passwordPlaceholder[language]}
                  value={password}
                  hasError={authError}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </InputContainer>
              <ButtonsContainer padding="20px 0 0">
                <RectangleButton
                  type="submit"
                  isAuthButton
                  disabled={!email || !password || (authError && authError !== AUTH_TWO_FACTOR_TIMEOUT_ERROR)}
                >
                  {strings.loginLabel[language]}
                </RectangleButton>
              </ButtonsContainer>
            </Form>
          )}
          {authError && (
            <InfoText error={authError}>
              {authLoading ? strings.loadingLabel[language] : ''}
              {apiErrors?.[authError?.name]?.[language] ?? authError?.[language] ?? authError}
            </InfoText>
          )}
          <Divider text="or" />
          <SignInWithGoogle />
          <SignInWithLinkedIn />
          <LinkToPage onClick={() => navigate(Path.signupPage)}>{strings.noAccountLabel[language]}</LinkToPage>
          <LinkToPage onClick={() => navigate(Path.resetPasswordPage)}>{strings.resetPassword[language]}</LinkToPage>
        </AuthContainer>
      </Container>
    </Wrapper>
  );
}
